import React, {useState} from 'react';

import {Button} from '@kit';

import type {CharacterAction} from '../../model/collect-effects';
import {formatDice, parseDice} from '../../model/dice';
import type {Procedure} from '../../model/procedures';
import {ChangeChoiceModal} from './procedures/change-choice-modal';
import {HealingPoolModal} from './procedures/healing-pool-modal';
import {OptionsModal} from './procedures/options-modal';
import {PactSlotRecoveryModal} from './procedures/pact-slot-recovery-modal';
import type {ProcedureModalProps} from './procedures/procedure-modal-props';
import {ResourceRecoveryModal} from './procedures/resource-recovery-modal';
import {SlotConversionModal} from './procedures/slot-conversion-modal';
import {SlotRecoveryModal} from './procedures/slot-recovery-modal';
import {SpellChoiceModal} from './procedures/spell-choice-modal';
import {SpellSwapModal} from './procedures/spell-swap-modal';
import {WildResurgenceModal} from './procedures/wild-resurgence-modal';
import {useFeatureAction} from './use-feature-action';

type ProcedureModals = {[Type in Procedure['type']]: React.FC<ProcedureModalProps<Type>>};

const PROCEDURE_MODALS: ProcedureModals = {
	slotRecovery: SlotRecoveryModal,
	resourceRecovery: ResourceRecoveryModal,
	pactSlotRecovery: PactSlotRecoveryModal,
	spellSwap: SpellSwapModal,
	changeChoice: ChangeChoiceModal,
	healingPool: HealingPoolModal,
	slotConversion: SlotConversionModal,
	wildResurgence: WildResurgenceModal,
	options: OptionsModal,
	spellChoice: SpellChoiceModal,
};

/** окна, которыми можно пользоваться и без оставшихся использований: обмен ячеек, смена выбора */
const ALWAYS_AVAILABLE = new Set<Procedure['type']>(['slotConversion', 'wildResurgence', 'changeChoice', 'spellSwap']);

type Props = {
	action: CharacterAction;
	procedure: Procedure;
};

/** кнопка «Применить», которая открывает окно процедуры особенности */
export const ProcedureButton: React.FC<Props> = ({action, procedure}) => {
	const {hasUses, canUse} = useFeatureAction(action);
	const [isOpen, setOpen] = useState(false);
	const isDisabled = hasUses && !canUse && !ALWAYS_AVAILABLE.has(procedure.type);
	const Modal = PROCEDURE_MODALS[procedure.type] as React.FC<ProcedureModalProps<Procedure['type']>>;
	const dice = procedure.type === 'resourceRecovery' && procedure.healing ? procedure.healing : undefined;

	return (
		<>
			<Button size="small" disabled={isDisabled} onClick={() => setOpen(true)}>
				{isDisabled ? 'Применено' : 'Применить'}
				{!isDisabled && dice && ` (${formatDice(parseDice(dice))})`}
			</Button>
			{isOpen && <Modal action={action} procedure={procedure} onClose={() => setOpen(false)} />}
		</>
	);
};
